import { ActionList, ConnectionItem, PopoverMenu } from "@wayflyer/flyui";
import { IconMoreVert16Line } from "@wayflyer/flyui-icons/16/line";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { MESSAGES } from "../message-definitions";

// ConnectionItem has no slot for a trailing popover, so this anchors the popover to the card's
// corner while the visible kebab (via ConnectionItem's own `actions`) just toggles it open.
const ANCHOR_STYLE = { position: "absolute", top: 0, right: 0, width: 1, height: 1 } as const;

type StatementRowProps = {
  className?: string;
  label: string;
  logo: ReactNode;
  onReassign: () => void;
  onRemove: () => void;
  isRemoveDisabled?: boolean;
};

export const StatementRow = ({ className, label, logo, onReassign, onRemove, isRemoveDisabled }: StatementRowProps) => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // The popover's own trigger can't be the real kebab (see ANCHOR_STYLE below), so on close we
  // restore focus to it ourselves rather than let the popover focus its invisible anchor.
  const handleOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      wrapperRef.current?.querySelector("button")?.focus();
    }
  };

  // ConnectionItem's action button isn't the popover's real trigger (see above), so react-aria
  // never applies its menu-relationship attributes there - mirror them onto it by hand so
  // screen readers still get the right semantics.
  useEffect(() => {
    const button = wrapperRef.current?.querySelector("button");
    button?.setAttribute("aria-haspopup", "menu");
    button?.setAttribute("aria-expanded", String(isMenuOpen));
  }, [isMenuOpen]);

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <ConnectionItem
        className={className}
        label={label}
        logo={logo}
        actions={[
          {
            id: "more",
            icon: IconMoreVert16Line,
            accessibilityLabel: intl.formatMessage(MESSAGES.moreOptionsAccessibilityLabel),
            onAction: () => setIsMenuOpen(true),
          },
        ]}
      />
      <PopoverMenu
        isOpen={isMenuOpen}
        onOpenChange={handleOpenChange}
        placement="bottom-end"
        trigger={<span style={ANCHOR_STYLE} tabIndex={-1} aria-hidden="true" />}
      >
        <ActionList
          items={[
            {
              id: "remove",
              content: intl.formatMessage(MESSAGES.removeMenuAction),
              disabled: isRemoveDisabled,
              onAction: () => {
                setIsMenuOpen(false);
                onRemove();
              },
            },
            {
              id: "reassign",
              content: intl.formatMessage(MESSAGES.reassignMenuAction),
              onAction: () => {
                setIsMenuOpen(false);
                onReassign();
              },
            },
          ]}
        />
      </PopoverMenu>
    </div>
  );
};
